import React from "react";
import { motion } from "framer-motion";
import { Heart, Handshake, Sparkles, Compass } from "lucide-react";
import { IMAGES } from "@/lib/site-data";

const VALUES = [
  {
    icon: Heart,
    title: "Empathy first",
    body: "We listen deeply before we propose. Every engagement starts with understanding the people behind the mission.",
  },
  {
    icon: Handshake,
    title: "Hands-on partnership",
    body: "We don't drop deliverables and disappear. We're in the room, in the work, in the wins — alongside you.",
  },
  {
    icon: Sparkles,
    title: "Rooted in dignity",
    body: "Solutions for justice system-impacted and underserved communities built on belonging and lived experience.",
  },
  {
    icon: Compass,
    title: "Implementation focused",
    body: "Strategy means nothing without execution. We translate vision into the operational realities of your team.",
  },
];

const Approach = () => {
  return (
    <section
      id="approach"
      data-testid="approach-section"
      className="relative py-24 md:py-32 bg-brand-ink text-brand-cream overflow-hidden"
    >
      {/* Decorative texture */}
      <div className="absolute inset-0 opacity-25 pointer-events-none">
        <img
          src={IMAGES.handsSeedling}
          alt=""
          className="w-full h-full object-cover"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-ink via-brand-ink/90 to-brand-blue/60" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-4xl">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-sans text-xs md:text-sm tracking-[0.25em] uppercase text-brand-periwinkle mb-6"
          >
            Our approach
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="font-serif font-light text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight"
          >
            We don't just advise —{" "}
            <span className="italic text-brand-periwinkle">
              we roll up our sleeves.
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="mt-8 max-w-2xl text-lg md:text-xl text-brand-cream/75 leading-relaxed font-sans"
          >
            Real problems get solved in real time. That means working alongside
            our clients — facilitating the workshop, writing the proposal,
            running the program — not just diagnosing what's wrong.
          </motion.p>
        </div>

        <div className="mt-16 md:mt-24 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {VALUES.map((v, idx) => {
            const Icon = v.icon;
            return (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: idx * 0.1 }}
                data-testid={`value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}
                className="relative border-t border-brand-cream/20 pt-6 group"
              >
                <div className="absolute -top-px left-0 w-12 h-px bg-brand-periwinkle group-hover:w-20 transition-all duration-500" />
                <Icon className="w-7 h-7 text-brand-periwinkle mb-5" strokeWidth={1.5} />
                <h3 className="font-serif text-xl md:text-2xl mb-3 leading-snug">
                  {v.title}
                </h3>
                <p className="text-sm md:text-[15px] text-brand-cream/70 leading-relaxed font-sans">
                  {v.body}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Approach;
